import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconAlertTriangle,
  IconBulb,
  IconChevronDown,
  IconCopy,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import ContentMarkdown, { type CitationResolver } from "../ContentMarkdown";
import type { AnswerCalloutTone } from "../../domains/gpt/answer";
import type { SupportedLanguage } from "../../../../api/src/shared/domain/language";

// The geometry of one "bullet-item" row, measured on the Figma nodes
// (6690:5867 / 6690:5882, panel 353 wide with 16px padding): `bullet-dot` is
// 6px wide at x=0 on a 20px line box, `bullet-content` starts at x=16, and rows
// sit 16px apart. The panel's own `gap-4` already opens the 16px under the
// header, so the list contributes NO margin above its first row — a top margin
// there stacks on that gap and doubles it.
//
// The dot is drawn as a `::before`, not as the list `::marker`: a marker's
// distance to the text is the browser's to choose (`list-style-position:
// outside` puts it at an offset no property controls), and that offset was
// visibly wider than the designed 10px. Absolutely positioning the glyph at the
// row's start edge, against a 16px text indent, reproduces the 6+10 split
// exactly. Scoped to `ul > li` so an ordered list keeps its own numbering.
const CALLOUT_BULLETS =
  "[&_p]:my-2 [&_ul]:my-0 [&_ol]:my-0 [&_ol]:ps-4 [&_ol]:list-decimal [&_ul]:list-none [&_ul]:ps-0 [&_ul>li]:relative [&_ul>li]:ps-4 [&_li]:my-0 [&_li]:py-0 [&_li+li]:mt-4 [&_li>p]:my-0 [&_ul>li]:before:absolute [&_ul>li]:before:start-0 [&_ul>li]:before:top-0 [&_ul>li]:before:text-ui-body [&_ul>li]:before:content-['•']";

// Per-tone surface, accent, glyph and body prose (Figma DS 5.0 "Prompt
// Panel/Key points" node 6690:5867 and "Prompt Panel/Caution" node 6690:5882).
// Full class strings — never interpolated — so Tailwind's JIT keeps both
// variants. A body is 13/20 prose whose text and bullets take the panel's own
// accent color.
//
// Only the caution panel is stroked, and it is stroked in the DS
// `color/opacity/default/200` — black at 6%, the same hairline every bordered
// card on the app carries. Clementine-50 is a paler wash than blueberry-50, so
// without it the panel dissolves into the white answer column; a solid grey
// (moss-200) reads as a card border instead and fights the accent.
const TONES = {
  key: {
    panel: "bg-ds-blueberry-50",
    Icon: IconBulb,
    default: {
      title: "text-ds-blueberry-800",
      body: `font-dm-sans text-ds-mint-800 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${CALLOUT_BULLETS} [&_ul>li]:before:[color:var(--color-ds-blueberry-800)] [&_ol>li::marker]:text-ds-blueberry-800`,
    },
    // The desktop mockups ("Key Points", answer column) set the whole panel in
    // blueberry-900, the body at 14/20.
    desktop: {
      title: "text-ds-blueberry-900",
      body: `font-dm-sans text-sm text-ds-blueberry-900 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${CALLOUT_BULLETS} [&_ul>li]:before:[color:var(--color-ds-blueberry-900)] [&_ol>li::marker]:text-ds-blueberry-900`,
    },
  },
  caution: {
    panel: "bg-ds-clementine-50 border border-ds-black/6",
    Icon: IconAlertTriangle,
    default: {
      title: "text-ds-clementine-700",
      body: `font-dm-sans text-ds-clementine-700 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${CALLOUT_BULLETS} [&_ul>li]:before:[color:var(--color-ds-clementine-700)] [&_ol>li::marker]:text-ds-clementine-700`,
    },
    desktop: {
      title: "text-ds-clementine-700",
      body: `font-dm-sans text-ds-clementine-700 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${CALLOUT_BULLETS} [&_ul>li]:before:[color:var(--color-ds-clementine-700)] [&_ol>li::marker]:text-ds-clementine-700`,
    },
  },
} as const satisfies Record<
  AnswerCalloutTone,
  {
    panel: string;
    Icon: typeof IconBulb;
    default: { title: string; body: string };
    desktop: { title: string; body: string };
  }
>;

// The panel's heading, per tone, in the answer's language, or the app's when the
// answer names none. A switch and not a `TONES` entry because the value is a
// `t()` call: `pnpm i18n:scan` only sees literal keys, so a title resolved
// through a lookup would be pruned from all eight locales.
function useCalloutTitle(
  tone: AnswerCalloutTone,
  language: SupportedLanguage | null
): string {
  const { i18n } = useTranslation();
  const t = i18n.getFixedT(language === null ? i18n.language : language);
  switch (tone) {
    case "key":
      return t("Key points");
    case "caution":
      return t("Caution");
    default:
      throw new Error(tone satisfies never);
  }
}

/**
 * One highlighted panel of a Juisci GPT answer: the blue "Key points" synthesis
 * the agent opens with, or the orange "Caution" caveat it closes with.
 *
 * A panel is NOT separate data. It is a slice of the answer's own markdown —
 * the `> [!KEY POINTS]` / `> [!CAUTION]` blockquote the prompt asks for, lifted
 * out by `parseAnswerBlocks` — so its body renders through `ContentMarkdown`
 * like the prose around it. That is what keeps a bullet's citations chipped,
 * numbered against the Sources tab and clickable: a bespoke renderer here would
 * have to reimplement all three.
 *
 * Tone drives everything visual AND the title, because they are not
 * independent — a "Caution" on a blueberry surface is a state that must not be
 * representable. The answer's language only picks the title's translation.
 */
export function CalloutPanel({
  variant,
  tone,
  language,
  text,
  math,
  streaming,
  citation,
  onCopy,
  className,
}: {
  // `desktop` is the desktop shell's panel, `default` the phone's.
  variant: "default" | "desktop";
  tone: AnswerCalloutTone;
  // The language of the answer, which the title follows. Null when the answer
  // names none: the title then follows the app's language.
  language: SupportedLanguage | null;
  // The panel body, as markdown: one bullet per point.
  text: string;
  math: boolean;
  // The answer is still being written, so a half-emitted citation href must not
  // navigate — the chips show as loading instead.
  streaming: boolean;
  // The turn's citation numbering: a panel cites the same sources the prose
  // around it does, so its chips carry the same numbers and open the same
  // sheet.
  citation: CitationResolver;
  // Copies the panel's own text. A synthesis or a caveat is the part readers
  // lift out of an answer, so it gets a copy of its own, separate from the
  // answer action bar.
  onCopy: () => void;
  className?: string;
}): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(true);
  const { panel, Icon } = TONES[tone];
  const { title: titleClass, body } = TONES[tone][variant];
  const title = useCalloutTitle(tone, language);

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 rounded-2xl p-4 text-footnote",
        panel,
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen((open) => !open);
          }}
          className={cn(
            "flex cursor-pointer items-center gap-1.5 bg-transparent p-0 text-left font-dm-sans text-ui-body font-medium",
            titleClass
          )}
        >
          <Icon stroke={1.5} className="size-5 shrink-0" />
          {title}
          <IconChevronDown
            stroke={1.5}
            className={cn(
              "size-4 shrink-0 transition-transform duration-150",
              isOpen ? null : "-rotate-90"
            )}
          />
        </button>
        <button
          type="button"
          onClick={onCopy}
          className={cn(
            "cursor-pointer bg-transparent p-0 opacity-70",
            titleClass
          )}
        >
          <IconCopy stroke={1.5} className="size-5 shrink-0" />
        </button>
      </div>
      {isOpen ? (
        <ContentMarkdown
          className={body}
          math={math}
          linkVariant="tag"
          streaming={streaming}
          citation={citation}
        >
          {text}
        </ContentMarkdown>
      ) : null}
    </div>
  );
}
